import React, { useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import Details from '../../../components/AddOrEditLoanFile/Details';
import Borrowers from '../../../components/AddOrEditLoanFile/Borrowers';
import Documents from '../../../components/AddOrEditLoanFile/Documents';
import EditDocument from '../../../components/AddOrEditLoanFile/Documents/EditDocument';
import Underwriting from '../../../components/AddOrEditLoanFile/Underwriting';
import Borrower from '../../../components/AddOrEditLoanFile/Borrower';
import Credit from '../../../components/AddOrEditLoanFile/Credit';

import { useUiStore } from '../../../store';
import LoanFileService from '../../../services/LoanFileService';
import BorrowerService from '../../../services/BorrowerService';

const createSecondarySidebarLinks = (
  id,
  borrowers,
  bankStatementCollections
) => {
  const middlePart = id ? `${id}` : 'create';
  const isDisabled = middlePart === 'create';

  const bankStatementSubLinks = [
    {
      title: 'Overview',
      url: `/loan-files/${middlePart}/underwriting/overview`,
    },
  ];

  if (bankStatementCollections && bankStatementCollections.length > 0) {
    bankStatementSubLinks.push({
      title: 'Bank Statements',
      url: `/loan-files/${middlePart}/underwriting/bank-statements`,
    });
  }

  return [
    {
      title: 'Details',
      url: `/loan-files/${middlePart}/details`,
    },
    {
      title: 'Borrowers',
      url: `/loan-files/${middlePart}/borrowers`,
      disabled: isDisabled,
      links: borrowers?.map((borrower) => ({
        title: `${borrower.firstName} ${borrower.lastName}`,
        url: `/loan-files/${middlePart}/borrowers/${borrower.id}`,
      })),
    },
    {
      title: 'Documents',
      url: `/loan-files/${middlePart}/documents`,
      disabled: isDisabled,
    },
    {
      title: 'Credit',
      url: `/loan-files/${middlePart}/credit`,
      disabled: isDisabled,
      links: [
        {
          title: 'Overview',
          url: `/loan-files/${middlePart}/credit/overview`,
        },
        {
          title: 'Credit Reports',
          url: `/loan-files/${middlePart}/credit/reports`,
        },
      ],
    },
    {
      title: 'Underwriting',
      url: `/loan-files/${middlePart}/underwriting`,
      disabled: isDisabled,
      links: bankStatementSubLinks,
    },
  ];
};

export default function AddOrEditLoanFilePage({ section }) {
  const { id, documentId } = useParams();
  const isEdit = !!id && id !== 'create';

  const { data: borrowers, refetch: refetchBorrowers } = useQuery({
    queryKey: ['borrowers', id],
    queryFn: () => BorrowerService.getBorrowersOfLoanFile(id),
    enabled: !!id,
  });

  const setSecondarySidebarLinks = useUiStore(
    (state) => state.setSecondarySidebarLinks
  );

  const { data: loanFile, refetch } = useQuery({
    queryKey: ['loanFile', id],
    queryFn: () => LoanFileService.getLoanFile(id),
    enabled: isEdit,
  });

  const {
    data: bankStatementCollections,
    refetch: refetchBankStatementCollections,
  } = useQuery({
    queryKey: ['loanFileBankStatementCollections', id],
    queryFn: () => LoanFileService.getLoanFileBankStatementCollections(id),
    enabled: isEdit,
  });

  const { mutate: updateLoanFile } = useMutation({
    mutationFn: (formValues) =>
      LoanFileService.updateLoanFile({ id, formValues }),
    onSuccess: () => {
      refetch();
    },
    onError: (error) => {
      console.log(error);
    },
  });

  // Override the secondary sidebar links with the ones specific to the AddOrEditLoanFilePage
  useEffect(() => {
    const secondarySidebarLinks = createSecondarySidebarLinks(
      id,
      borrowers,
      bankStatementCollections
    );

    setSecondarySidebarLinks(secondarySidebarLinks);

    return () => {
      setSecondarySidebarLinks([]);
    };
  }, [bankStatementCollections, borrowers, id, setSecondarySidebarLinks]);

  return (
    <>
      {section === 'details' && (
        <Details
          loanFile={loanFile}
          isEdit={isEdit}
          loanFileId={id}
          updateLoanFile={updateLoanFile}
        />
      )}
      {section === 'borrowers' && <Borrowers loanFileId={id} isEdit={isEdit} />}
      {section === 'borrower' && <Borrower />}
      {section === 'documents' && (
        <>
          {documentId ? (
            <EditDocument loanFileId={id} loanFile={loanFile} />
          ) : (
            <Documents loanFileId={id} isEdit={isEdit} loanFile={loanFile} />
          )}
        </>
      )}
      {section === 'underwriting' && (
        <Underwriting
          loanFileId={id}
          isEdit={isEdit}
          loanFile={loanFile}
          bankStatementCollections={bankStatementCollections}
          refetch={refetchBankStatementCollections}
        />
      )}
      {section === 'credit' && (
        <Credit
          loanFileId={id}
          loanFile={loanFile}
          borrowers={borrowers}
          refetchBorrowers={refetchBorrowers}
        />
      )}
    </>
  );
}
